import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import SeatingChartDesignerModal from './SeatingChartDesignerModal';
import { toastError, toastSuccess } from '../../../shared/toastHelper';

// The server explains why a chart was rejected (for example a key already
// connected to another venue) in a 422 { errors: [...] }. That message is what
// tells the user what to do next, so prefer it over the generic copy.
export const createChartErrorMessage = (error) => {
  const data = error?.response?.data;

  if (data?.errors?.length) {
    return data.errors.join(' ');
  }

  return data?.error || 'There was an error creating the chart.';
};

const CreateSeatingChartDesigner = ({
  show,
  onClose,
  teamId,
  venueOwnershipId,
  context,
  onChartCreated,
}) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [chartKey, setChartKey] = useState();

  const registerNewChart = useCallback(async (chartKey) => {
    try {
      setIsSaving(true);
      const url = `/teams/${teamId}/venue_ownerships/${venueOwnershipId}/seating_charts`;
      const { data } = await axios.post(url, { seating_chart: { context, seatsio_id: chartKey } });
      toastSuccess('Chart created successfully.');
      onChartCreated && onChartCreated(data.seating_chart);
      setHasUnsavedChanges(false);
      setIsSaving(false);
      onClose();
    } catch (err) {
      setIsSaving(false);
      toastError(createChartErrorMessage(err));
    }
  }, [teamId, venueOwnershipId, context, onChartCreated, onClose]);

  const handleSaveAndExit = useCallback(async () => {
    if (hasUnsavedChanges && chartKey) {
      await registerNewChart(chartKey);
    }
    onClose();
  }, [hasUnsavedChanges, chartKey, registerNewChart, onClose]);


  return (
    <SeatingChartDesignerModal
      show={show}
      onSaveAndExit={handleSaveAndExit}
      isSaving={isSaving}
      editorKeyUrl={`/teams/${teamId}/venue_ownerships/${venueOwnershipId}/seating_charts/editor_key`}
      chartKey={undefined}
      readOnlyName={false}
      hasUnsavedChanges={hasUnsavedChanges}
      setHasUnsavedChanges={setHasUnsavedChanges}
      onChartCreated={chartKey => {
        setChartKey(chartKey);
      }}
    />
  );
};

CreateSeatingChartDesigner.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  teamId: PropTypes.number.isRequired,
  venueOwnershipId: PropTypes.number.isRequired,
  context: PropTypes.string.isRequired,
  onChartCreated: PropTypes.func,
};

export default CreateSeatingChartDesigner;


